// Single source of truth for the cloud API origin. Imported by every surface
// that talks to the server (App, Shell, Landing, ApplyShell, OperatorShell) so a
// new deployment URL — e.g. when the pilot ends and Aurora moves to a public
// domain — changes in ONE place instead of five. Overridable per-session via
// localStorage[SERVER_STORAGE_KEY], which the dev/test harness sets to point at
// a local backend.
//
// Same-origin hosts: when this SPA is served by a FastAPI app on the VPS (not
// Cloudflare Pages), it must talk to its OWN origin (same-origin, no CORS) so
// the Cloudflare Access JWT rides the request and authenticates the operator
// with no token. Two such hosts:
//   - staging-app.plaintosee.cc — hosted-staging (Option B), full UI mirror
//   - ops.plaintosee.cc         — the permanent gated operator console
// Every other host (pilot, preview, prod Pages) uses the prod API, so the same
// built bundle works on Pages and on the VPS-served hosts.
export const STAGING_HOST = "staging-app.plaintosee.cc";
export const OPERATOR_HOST = "ops.plaintosee.cc";
const SAME_ORIGIN_HOSTS = new Set([STAGING_HOST, OPERATOR_HOST]);

// A host the SPA is served from same-origin by the FastAPI app (not Cloudflare
// Pages). Both the default API base (below) AND the operator API base
// (OperatorShell.operatorServerBase) branch on this — they MUST agree, or the
// operator surface calls the wrong base (the Pages /operator/api bridge that
// only exists on cloud.plaintosee.cc). Keep this the single source of truth.
export function isSameOriginHost(hostname: string): boolean {
  return SAME_ORIGIN_HOSTS.has(hostname);
}

function resolveDefaultServer(): string {
  if (
    typeof window !== "undefined" &&
    isSameOriginHost(window.location.hostname)
  ) {
    return window.location.origin;
  }
  return "https://app.plaintosee.cc";
}

export const DEFAULT_SERVER = resolveDefaultServer();
export const SERVER_STORAGE_KEY = "aurora.cloud.serverUrl";

// The API origin a surface should actually call right now: the per-session
// localStorage override when set, else DEFAULT_SERVER. In dev (localhost)
// it is forced to same-origin so the Vite proxy forwards the backend route
// prefixes — a stale override from an earlier test session would otherwise
// bypass the proxy. Shared by Shell (gate, config, agreement) and the
// site-wide OutageBanner, which must probe the SAME host the wizard talks to:
// a same-origin "/healthz" on cloud.plaintosee.cc is answered by Pages with
// the SPA's index.html, so a probe there can never see the API at all.
export function resolveServer(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return window.location.origin;
    }
  }
  try {
    const stored = localStorage.getItem(SERVER_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_SERVER;
}
